// Профиль: ник, город, способы входа (привязать / отвязать / объединить), выход.
import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { api } from '../platform/api.ts';
import { APP_URL } from '../platform/config.ts';
import { inTelegram } from '../platform/telegram.ts';
import { useMe, type Method } from '../app/state.tsx';
import { Screen, Busy, toast } from '../app/ui.tsx';
import { useTgPoll } from '../app/Login.tsx';
import { t, errText } from '../i18n/index.ts';

// ответ привязки: готово / нужно подтвердить объединение
export type LinkResult = { linked?: boolean; merged?: boolean; need_confirm?: boolean; nonce?: string; text?: string };

export function MergeAsk({ ask, onDone }: { ask: LinkResult; onDone: (r: LinkResult | null) => void }) {
  return (
    <div className="modal" role="dialog" aria-modal>
      <div className="card">
        <p>{ask.text}</p>
        <div className="row">
          <Busy onClick={async () => { try { onDone(await api('link_confirm', { nonce: ask.nonce })); } catch (e) { toast(errText(e)); onDone(null); } }}>{t('profile.merge')}</Busy>
          <button type="button" className="btn ghost" onClick={() => { api('link_cancel', { nonce: ask.nonce }).catch(() => {}); onDone(null); }}>{t('common.cancel')}</button>
        </div>
      </div>
    </div>
  );
}

export function Profile() {
  const { me, reload, logout } = useMe();
  const nav = useNavigate();
  const [nick, setNick] = useState(me?.profile.nick ?? '');
  const [city, setCity] = useState(me?.profile.city ?? '');
  const [email, setEmail] = useState<string | null>(null);   // null — форма привязки почты закрыта
  const [code, setCode] = useState('');
  const [sent, setSent] = useState(false);
  const [ask, setAsk] = useState<LinkResult | null>(null);

  const linked = async (r: LinkResult | null) => {
    setAsk(null);
    if (!r) return;
    if (r.need_confirm) { setAsk(r); return; }
    toast(t(r.merged ? 'profile.merged' : 'profile.linked'));
    setEmail(null); setSent(false); setCode('');
    await reload();
  };
  const tgPoll = useTgPoll(linked);

  if (!me) return <Screen title={t('profile.title')} back="/"><Link className="btn" to="/login">{t('home.login')}</Link></Screen>;
  const has = (p: Method['provider']) => me.methods.some(m => m.provider === p);

  return (
    <Screen title={t('profile.title')} back="/">
      <section className="card">
        {me.profile.is_guest && <p className="badge">{t('profile.guest')}</p>}
        <label>{t('profile.nick')}<input value={nick} maxLength={40} onChange={e => setNick(e.target.value)} /></label>
        <label>{t('profile.city')}<input value={city} maxLength={60} onChange={e => setCity(e.target.value)} /></label>
        <Busy onClick={async () => {
          try { await api('profile_save', { nick, city }); await reload(); toast(t('profile.saved')); } catch (e) { toast(errText(e)); }
        }}>{t('common.save')}</Busy>
      </section>

      <section className="card">
        <h2>{t('profile.methods')}</h2>
        <ul className="methods">
          {me.methods.map(m => (
            <li key={m.provider + m.subject}>
              <span><b>{t(`profile.method.${m.provider}`)}</b> {m.label}</span>
              {me.methods.length > 1 && !(inTelegram() && m.provider === 'telegram') && (
                <Busy className="link" onClick={async () => {
                  if (!window.confirm(t('profile.unlinkAsk', { name: m.label }))) return;
                  try { await api('unlink', { provider: m.provider, subject: m.subject }); await reload(); } catch (e) { toast(errText(e)); }
                }}>{t('profile.unlink')}</Busy>
              )}
            </li>
          ))}
        </ul>

        {!has('email') && me.available.email && (email === null
          ? <button type="button" className="btn ghost" onClick={() => setEmail('')}>{t('profile.link.email')}</button>
          : !sent ? (
            <div className="stack">
              <input type="email" inputMode="email" placeholder={t('login.emailPlaceholder')} value={email} onChange={e => setEmail(e.target.value)} />
              <Busy onClick={async () => { try { await api('link_email_start', { email }); setSent(true); } catch (e) { toast(errText(e)); } }}>{t('login.sendCode')}</Busy>
            </div>
          ) : (
            <div className="stack">
              <p className="muted">{t('login.codeSent', { email })}</p>
              <input inputMode="numeric" maxLength={6} placeholder={t('login.codePlaceholder')} value={code} onChange={e => setCode(e.target.value.replace(/\D/g, ''))} />
              <Busy onClick={async () => { try { await linked(await api('link_email_verify', { email, code })); } catch (e) { toast(errText(e)); } }}>{t('login.signIn')}</Busy>
            </div>
          ))}
        {!has('telegram') && me.available.telegram && (tgPoll.wait
          ? <p><span className="spin" /> {t('login.tgWaiting')}</p>
          : <Busy className="btn ghost" onClick={async () => { try { tgPoll.start(await api('link_tg_start')); } catch (e) { toast(errText(e)); } }}>{t('profile.link.telegram')}</Busy>)}
        {!has('vk') && me.available.vk && (
          <Busy className="btn ghost" onClick={async () => {
            try { location.href = (await api('link_vk_start', { redirect_uri: APP_URL })).url; } catch (e) { toast(errText(e)); }
          }}>{t('profile.link.vk')}</Busy>
        )}
      </section>

      {me.role && <Link className="btn ghost wide" to="/admin">{t('profile.admin')}</Link>}
      {!inTelegram() && <button type="button" className="btn ghost wide" onClick={async () => { await logout(); nav('/'); }}>{t('profile.logout')}</button>}
      {ask && <MergeAsk ask={ask} onDone={linked} />}
    </Screen>
  );
}
